// Iter123k164 · Credius · Landing page pubblica "Accetta / Rifiuta incarico" da link email.
// Nessuna autenticazione richiesta: la validità è garantita dal token nel path.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Scale, FileText, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMBLEM = "/ginius-emblem.png";

export default function CrediusIncaricoPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [showRefuse, setShowRefuse] = useState(false);
  const [motivo, setMotivo] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/credius/incarico/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Link non valido o scaduto."))
      .finally(() => setLoading(false));
  }, [token]);

  const accetta = async () => {
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/public/credius/incarico/${token}/accetta`);
      setDone({ ok: true, message: `Incarico ACCETTATO per la pratica ${res.numero}. La lettera di incarico è stata generata e archiviata.`});
    } catch (e) {
      setDone({ ok: false, message: e.response?.data?.detail || "Operazione non riuscita. Riprova." });
    } finally { setBusy(false); }
  };

  const rifiuta = async () => {
    if (!motivo.trim()) {
      setDone({ ok: false, message: "Devi indicare un motivo per rifiutare l'incarico." });
      return;
    }
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/public/credius/incarico/${token}/rifiuta`, { motivo });
      setDone({ ok: true, message: `Incarico RIFIUTATO per la pratica ${res.numero}. Lo Studio è stato notificato.` });
    } catch (e) {
      setDone({ ok: false, message: e.response?.data?.detail || "Operazione non riuscita. Riprova." });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#1A1818] flex flex-col items-center px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <img src={EMBLEM} alt="Ginius" className="w-12 h-12 rounded-xl ring-2 ring-[#D4AF5A]/70" />
        <div className="leading-none">
          <div className="font-brand font-bold text-3xl text-brand" style={{ WebkitTextStroke: "0.5px #fff" }}>
            Ginius<span className="text-[#D4AF5A]">.</span>
          </div>
          <div className="text-xs text-[#E7C9A0]">Credius — Gestione affidamenti</div>
        </div>
      </div>

      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6" data-testid="credius-incarico-card">
        {loading && (
          <div className="flex items-center justify-center py-10 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-8 text-red-700 font-semibold" data-testid="credius-incarico-error">{error}</div>
        )}

        {!loading && data && !done && (
          <>
            <div className="flex items-center gap-2 text-brand font-bold text-lg mb-1">
              <Scale className="w-6 h-6" /> Pratica {data.numero}
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3" data-testid="credius-incarico-oggetto">{data.oggetto || "(oggetto non specificato)"}</h1>

            <div className="space-y-2 text-slate-700 text-sm mb-5">
              {data.settore && (
                <div className="flex items-start gap-1.5"><FileText className="w-4 h-4 mt-0.5 text-slate-400" />
                  <span><span className="font-semibold">Settore:</span> {data.settore} · <span className="font-semibold">Tipo:</span> {data.tipo || "—"}</span>
                </div>
              )}
              <div><span className="font-semibold">Avvocato affidatario:</span> {data.avvocato_nome || "—"}</div>
              {data.coaffidatario_nome && (
                <div><span className="font-semibold">Co-affidatario:</span> {data.coaffidatario_nome}</div>
              )}
              {data.clienti_summary && (
                <div className="flex items-start gap-1.5"><Users className="w-4 h-4 mt-0.5 text-slate-400" />
                  <span><span className="font-semibold">Clienti:</span> {data.clienti_summary}</span>
                </div>
              )}
            </div>

            {!data.azione_disponibile ? (
              <div className="text-center py-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 font-semibold" data-testid="credius-incarico-gia-gestito">
                {data.consumed
                  ? "Questo link è già stato utilizzato."
                  : `L'incarico non è più in attesa di accettazione (stato attuale: ${data.incarico_stato}).`}
              </div>
            ) : !showRefuse ? (
              <div className="space-y-3">
                <p className="text-center text-slate-600 text-sm">Confermi di accettare questo incarico?<br />All'accettazione la lettera di incarico verrà generata e archiviata automaticamente.</p>
                <button data-testid="credius-incarico-accetta-btn" disabled={busy} onClick={accetta}
                  className="w-full h-14 rounded-xl bg-green-700 hover:bg-green-800 text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60">
                  <CheckCircle2 className="w-6 h-6" /> {busy ? "Attendere…" : "Accetto l'incarico"}
                </button>
                <button data-testid="credius-incarico-rifiuta-open" disabled={busy} onClick={() => setShowRefuse(true)}
                  className="w-full h-12 rounded-xl border-2 border-red-300 text-red-700 font-semibold flex items-center justify-center gap-2">
                  <XCircle className="w-5 h-5" /> Rifiuto l'incarico
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-slate-700 text-sm font-semibold">Motivo del rifiuto <span className="text-red-600">*</span></p>
                <textarea data-testid="credius-incarico-rifiuta-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)}
                  className="w-full border-2 border-slate-300 rounded-lg p-3 h-28 text-sm"
                  placeholder="Es. conflitto di interessi, sovraccarico agenda, competenza territoriale…" required />
                <button data-testid="credius-incarico-rifiuta-conferma" disabled={busy || !motivo.trim()} onClick={rifiuta}
                  className="w-full h-12 rounded-xl bg-red-700 hover:bg-red-800 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                  <XCircle className="w-5 h-5" /> {busy ? "Attendere…" : "Conferma rifiuto"}
                </button>
                <button onClick={() => { setShowRefuse(false); setMotivo(""); }} className="w-full h-10 text-slate-500">Annulla</button>
              </div>
            )}
          </>
        )}

        {done && (
          <div className="text-center py-8" data-testid="credius-incarico-done">
            {done.ok
              ? <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-3" />
              : <XCircle className="w-16 h-16 text-amber-500 mx-auto mb-3" />}
            <p className="text-lg font-semibold text-slate-800">{done.message}</p>
            <p className="text-sm text-slate-500 mt-2">Puoi chiudere questa pagina.</p>
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-6">Credius by Ginius · Gestione pratiche legali</p>
    </div>
  );
}
